import { useCallback, useEffect, useState } from 'react'
import { Control, Controller, FieldValues, Path } from 'react-hook-form'

import { Autocomplete, Textfield } from '@clientbase/clientbase-library'
import { Box } from '@mui/material'
import { DataService } from 'api/DataService'

type Bank = {
  name: string
  uuid: string
  compe?: string
}

type AutoCompleteBankProps<T extends FieldValues> = {
  control: Control<T, object>
  name: Path<T>
}

const AutoCompleteBank = <T extends FieldValues>({
  control,
  name
}: AutoCompleteBankProps<T>) => {
  const [banks, setBanks] = useState<Bank[]>([])

  const getAutoCompleteBanks = useCallback(async () => {
    const responseCustumersAutocomplete = await DataService({
      type: 'GET',
      url: '/v1/banks'
    })

    if (responseCustumersAutocomplete.status === 200) {
      const banks = responseCustumersAutocomplete.data.records.map(
        (record: any) => ({
          name: record.name,
          uuid: record.uuid,
          compe: record.compe || record.code || ''
        })
      )

      setBanks(banks)
    }
  }, [])

  useEffect(() => {
    getAutoCompleteBanks()
  }, [])

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <Autocomplete
          id="bank_select"
          onChange={(_, value) => onChange(value)}
          value={value}
          options={banks}
          noOptionsText="Nenhum banco encontrado"
          autoHighlight
          getOptionLabel={(option) => {
            const bank = option as Bank
            return bank.compe ? `${bank.compe} - ${bank.name}` : bank.name
          }}
          isOptionEqualToValue={(option, value) =>
            (option as Bank).uuid === (value as Bank).uuid
          }
          renderOption={(props, option) => {
            const optionTyped = option as Bank
            return (
              <Box component="li" {...props}>
                {optionTyped.compe
                  ? `${optionTyped.compe} - ${optionTyped.name}`
                  : optionTyped.name}
              </Box>
            )
          }}
          renderInput={(params) => (
            <Textfield
              label="Em qual banco você quer receber os seus pagamentos?"
              error={!!error?.message}
              helperText={error?.message}
              placeholder="Selecione o banco"
              {...params}
            />
          )}
        />
      )}
    />
  )
}

export default AutoCompleteBank
